/* Smart Flow Services - 3D Skyscraper Scene Orchestrator (js/scene.js)
   Modular orchestrator managing:
   - Building architectural model (js/building.js)
   - Vertical floor stacking on Y axis:
       * Street Level (Y: -6 to 4)
       * Server Farm Floor / Hall (Y: 4 to 18, center base Y: 4)
       * Boardroom Suite (Y: 18 to 30, center base Y: 18)
       * Penthouse / Executive Suite (Y: 30 to 42, center base Y: 30)
   - Smooth continuous vertical camera tweening (no instant #room-fade teleports)
   - Exposes window.SFScene { goTo, goToFloor, enterFloorInterior, onRackHover, showProjectLogo, clearBoardLogo, getRackScreenPos }
*/

import * as THREE from "three";
import { EffectComposer } from "three/addons/postprocessing/EffectComposer.js";
import { RenderPass } from "three/addons/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/addons/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/addons/postprocessing/OutputPass.js";
import { buildBuilding } from "./building.js";
import {
  buildBoardroom,
  makeProjectLogoTexture,
  makeClientReviewTexture,
} from "./boardroom.js";
import { buildBots } from "./bots.js";
import { buildPenthouse } from "./penthouse.js";
import { buildStreet } from "./street.js";

const ACCENT = 0x66ccf0; // logo cyan, matches --accent in styles.css
const BG = 0x070b12;
const BG_DAY = 0x0a3275; // Deep royal blue top
const FOG_DAY = 0x7bb8ec; // Calm atmospheric blue horizon fog (not blinding white)
const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Builds a 3D architectural sky dome sphere surrounding the entire scene.
 * In Daytime: Deep rich blue at top (#082659 -> #1b62cc -> #3b8fe8) that smoothly
 * transitions into a balanced day blue and gentle warm horizon tint (#8ec7f8 -> #b8dcfa).
 * In Nighttime: Deep midnight navy with twinkling stars.
 */
function createSkyDome(THREE, isDay) {
  const geo = new THREE.SphereGeometry(180, 32, 24);
  const c = document.createElement("canvas");
  c.width = 2048;
  c.height = 1024;
  const g = c.getContext("2d");

  if (isDay) {
    // Sky gradient from Zenith (Y=0) to Nadir (Y=1024)
    const sky = g.createLinearGradient(0, 0, 0, 1024);
    sky.addColorStop(0.0, "#082659"); // Deep navy blue zenith (high sky)
    sky.addColorStop(0.18, "#0d439b"); // Deep royal blue
    sky.addColorStop(0.38, "#1b6ed8"); // Vibrant open day blue
    sky.addColorStop(0.52, "#4a9fed"); // Smooth azure mid-sky
    sky.addColorStop(0.65, "#7abaf2"); // Balanced, non-blinding horizon blue
    sky.addColorStop(0.75, "#9ecdf6"); // Gentle soft sky tint
    sky.addColorStop(0.88, "#6baae6"); // Soft atmosphere ground reflection
    sky.addColorStop(1.0, "#4a8ecc");
    g.fillStyle = sky;
    g.fillRect(0, 0, 2048, 1024);

    // Subtle soft warm sunrise glow at the far right horizon (gentle accent, no harsh blinding wash)
    g.globalCompositeOperation = "lighter";
    const sunX = 1560;
    const sunY = 720;
    const sunGlow = g.createRadialGradient(sunX, sunY, 0, sunX, sunY, 560);
    sunGlow.addColorStop(0.0, "rgba(254, 240, 138, 0.20)"); // Very soft gentle yellow
    sunGlow.addColorStop(0.4, "rgba(255, 237, 213, 0.08)"); // Subtle peach-gold
    sunGlow.addColorStop(1.0, "rgba(255, 255, 255, 0.0)");
    g.fillStyle = sunGlow;
    g.fillRect(0, 0, 2048, 1024);
    g.globalCompositeOperation = "source-over";
  } else {
    // Night sky
    const sky = g.createLinearGradient(0, 0, 0, 1024);
    sky.addColorStop(0.0, "#03060a");
    sky.addColorStop(0.45, "#09121d");
    sky.addColorStop(0.58, "#0e1e30");
    sky.addColorStop(1.0, "#04070c");
    g.fillStyle = sky;
    g.fillRect(0, 0, 2048, 1024);

    // HD Pinpoint Stars: high density, tiny crisp sub-pixel/1px dots with natural brightness variation
    const starCount = 380;
    for (let i = 0; i < starCount; i++) {
      const sx = Math.random() * 2048;
      const sy = Math.random() * 560; // Concentrated in upper dome/sky
      const rand = Math.random();

      let starR, a, col;
      if (rand < 0.7) {
        // Fine tiny pinpricks (distant background field)
        starR = 0.45 + Math.random() * 0.35;
        a = 0.25 + Math.random() * 0.55;
        col = "255, 255, 255";
      } else if (rand < 0.92) {
        // Crisp mid-brightness stars
        starR = 0.75 + Math.random() * 0.4;
        a = 0.6 + Math.random() * 0.35;
        col = rand < 0.82 ? "240, 248, 255" : "255, 248, 220";
      } else {
        // Few prominent sharp crystal stars
        starR = 1.0 + Math.random() * 0.4;
        a = 0.85 + Math.random() * 0.15;
        col = "255, 255, 255";

        // Subtle HD diffraction cross glow only for the few brightest stars
        g.strokeStyle = `rgba(255, 255, 255, ${(a * 0.25).toFixed(3)})`;
        g.lineWidth = 0.6;
        g.beginPath();
        g.moveTo(sx - 3, sy);
        g.lineTo(sx + 3, sy);
        g.moveTo(sx, sy - 3);
        g.lineTo(sx, sy + 3);
        g.stroke();
      }

      g.fillStyle = `rgba(${col}, ${a.toFixed(3)})`;
      g.beginPath();
      g.arc(sx, sy, starR, 0, Math.PI * 2);
      g.fill();
    }
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.mapping = THREE.EquirectangularReflectionMapping;

  const mat = new THREE.MeshBasicMaterial({
    map: tex,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });

  const dome = new THREE.Mesh(geo, mat);
  dome.position.set(0, 15, 0); // Center around middle of the skyscraper
  return { dome, tex };
}

/** Current local hour in Israel (DST handled by the Intl timezone). */
function israelHour() {
  try {
    return Number(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Jerusalem",
        hour: "2-digit",
        hour12: false,
      }).format(new Date()),
    );
  } catch (_) {
    const d = new Date();
    return (d.getUTCHours() + 3) % 24;
  }
}

function isDayInIsrael() {
  if (typeof window !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    if (params.get("night") === "1" || params.get("time") === "night")
      return false;
    if (params.get("day") === "1" || params.get("time") === "day") return true;
  }
  const h = israelHour();
  return h >= 6 && h < 19;
}

// Camera poses mapped per 2D section and 3D Tower Floors
const POSES = {
  // Legacy section shortcuts (mapped inside floors)
  hero: { pos: [0, 12.0, 7.5], look: [0, 11.3, 0] },
  services: { pos: [-2.9, 11.7, 3.0], look: [-4.2, 11.3, -1.5] },
  about: { pos: [0, 12.0, 2.8], look: [0, 11.3, 0] },
  contact: { pos: [2.9, 11.7, 3.0], look: [4.2, 11.3, -1.5] },
  process: { pos: [-1.3, 11.7, -2.5], look: [-2.6, 11.3, -7] },
  faq: { pos: [1.3, 11.7, -2.5], look: [2.6, 11.3, -7] },
  door: { pos: [0, 25.1, -16], look: [0, 23.5, -21] },
  work: { pos: [0, 24.9, -15], look: [0, 24.0, -25] },
  // SHOW ON BOARD: a gentle push toward the deal-room board (10 units away),
  // not a close-up - the whole board + logo stays comfortably in frame.
  board: { pos: [-2.5, 24.9, -15], look: [-2.5, 24.8, -25] },
  // Clients shares the Boardroom deal-room: same board, mirrored camera angle.
  clients: { pos: [2.5, 24.9, -15], look: [2.5, 24.8, -25] },

  // Tower Exterior Floor Poses (Stacked on Y axis)
  street: { pos: [0, 5.0, 34], look: [0, 4.5, 0] },
  tower_server: { pos: [0, 17.0, 26], look: [0, 16.0, 0] },
  tower_boardroom: { pos: [0, 29.0, 24], look: [0, 28.0, 0] },
  tower_penthouse: { pos: [0, 42.0, 29], look: [0, 39.0, 0] },

  // Tower Interior Poses (Gliding directly inside each floor's envelope)
  lobby_interior: { pos: [0, 2.7, 11.2], look: [0, 1.45, 1.4] },
  server_interior: { pos: [0, 12.0, 7.5], look: [0, 11.3, 0] },
  boardroom_interior: { pos: [0, 24.9, -3], look: [0, 24.0, -13] },
  penthouse_interior: { pos: [0, 36.5, 3.5], look: [0, 35.8, -2.5] },
};

// Clickable nav racks in Server Hall (local offsets from Server Hall base Y: 10.0)
const NAV_RACKS = {
  services: [-4.2, 10.0, -1.5],
  contact: [4.2, 10.0, -1.5],
  process: [-2.6, 10.0, -7],
  faq: [2.6, 10.0, -7],
};

let FACE_TEX = null;
function makeFaceTexture() {
  const c = document.createElement("canvas");
  c.width = 256;
  c.height = 512;
  const g = c.getContext("2d");

  // Body
  g.fillStyle = "#16202c";
  g.fillRect(0, 0, 256, 512);

  // Top status bezel
  g.fillStyle = "#233047";
  g.fillRect(14, 8, 228, 34);
  g.fillStyle = "#0a0f18";
  g.fillRect(14, 20, 228, 9);
  g.fillStyle = "#3a4c66";
  g.fillRect(14, 8, 228, 2);

  // Status LEDs
  g.fillStyle = "#3ddc84";
  g.beginPath();
  g.arc(206, 14, 3, 0, 7);
  g.fill();
  g.fillStyle = "#ff5f56";
  g.beginPath();
  g.arc(218, 14, 3, 0, 7);
  g.fill();
  g.fillStyle = "#66ccf0";
  g.beginPath();
  g.arc(230, 14, 3, 0, 7);
  g.fill();

  // Drive bays
  for (let i = 0; i < 8; i++) {
    const y = 44 + i * 52;
    g.fillStyle = "#202b39";
    g.fillRect(16, y, 224, 44);
    g.fillStyle = "#0a0f16";
    g.fillRect(16, y + 44, 224, 6);
    g.fillStyle = "#0e1520";
    for (let v = 0; v < 11; v++) g.fillRect(24 + v * 11, y + 11, 7, 22);
    g.fillStyle = "#33435a";
    g.fillRect(188, y + 15, 34, 12);
    g.fillStyle = "#17202c";
    g.fillRect(194, y + 18, 22, 6);
    g.fillStyle = "#0a0f16";
    g.beginPath();
    g.arc(182, y + 33, 3.2, 0, 7);
    g.fill();
  }

  // Bottom grille
  g.fillStyle = "#0d1420";
  g.fillRect(16, 460, 224, 44);
  for (let v = 0; v < 12; v++) g.fillRect(24, 466 + v * 3, 208, 1);
  g.fillStyle = "#1c3a2a";
  g.beginPath();
  g.arc(96, 478, 8, 0, 7);
  g.fill();
  g.fillStyle = "#3ddc84";
  g.beginPath();
  g.arc(96, 478, 4, 0, 7);
  g.fill();
  g.fillStyle = "#3a1c1c";
  g.beginPath();
  g.arc(160, 478, 8, 0, 7);
  g.fill();
  g.fillStyle = "#ff5f56";
  g.beginPath();
  g.arc(160, 478, 4, 0, 7);
  g.fill();

  // Corner screws
  g.fillStyle = "#33435a";
  for (const [sx, sy] of [
    [18, 10],
    [234, 10],
    [18, 502],
    [234, 502],
  ]) {
    g.beginPath();
    g.arc(sx, sy, 2, 0, 7);
    g.fill();
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function makeLabelTexture(section) {
  const c = document.createElement("canvas");
  c.width = 512;
  c.height = 128;
  const g = c.getContext("2d");
  g.font = '700 84px "JetBrains Mono", ui-monospace, monospace';
  g.textBaseline = "middle";
  g.textAlign = "center";
  g.fillStyle = "rgba(150, 200, 235, 0.85)";
  g.fillText(section.toUpperCase(), 256, 64, 480);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

function makeRack({ hero = false } = {}) {
  const group = new THREE.Group();
  const w = hero ? 1.7 : 1.2;
  const h = hero ? 3.0 : 2.4;

  const body = new THREE.Mesh(
    new THREE.BoxGeometry(w, h, 0.9),
    new THREE.MeshStandardMaterial({
      color: 0x202a36,
      roughness: 0.75,
      metalness: 0.55,
    }),
  );
  body.position.y = h / 2;
  group.add(body);

  if (!FACE_TEX) FACE_TEX = makeFaceTexture();
  const face = new THREE.Mesh(
    new THREE.PlaneGeometry(w * 0.9, h * 0.86),
    new THREE.MeshStandardMaterial({
      map: FACE_TEX,
      roughness: 0.85,
      metalness: 0.4,
      polygonOffset: true,
      polygonOffsetFactor: -1,
    }),
  );
  face.position.set(0, h * 0.47, 0.455);
  group.add(face);

  const ledMat = new THREE.MeshBasicMaterial({
    color: ACCENT,
    toneMapped: false,
    fog: false,
  });
  const strip = new THREE.Mesh(new THREE.PlaneGeometry(w * 0.62, 0.03), ledMat);
  strip.position.set(0, h * 0.859, 0.46);
  group.add(strip);

  group.userData.ledMat = ledMat;
  group.userData.ledBright = 1;
  group.userData.phase = Math.random() * Math.PI * 2;
  group.userData.blinkFreq = 1.2 + Math.random() * 3.0;
  group.userData.w = w;
  group.userData.h = h;
  return group;
}

function buildCeiling(scene, racks, baseY = 10.0) {
  // F1 ceiling slab (slab2) bottom face sits at Y = 21.7 (22.0 - 0.3)
  const CEIL_Y = 21.7;

  const beamMat = new THREE.MeshStandardMaterial({
    color: 0x11161e,
    roughness: 0.7,
    metalness: 0.5,
  });
  // Industrial suspended light fixtures + structural conduit rails
  const housingGeo = new THREE.BoxGeometry(1.2, 0.2, 0.6);
  const faceGeo = new THREE.PlaneGeometry(1.0, 0.45);
  const faceMat = new THREE.MeshBasicMaterial({
    color: 0x9be0ff,
    toneMapped: false,
    fog: false,
  });

  const conduitGeo = new THREE.CylinderGeometry(0.02, 0.02, 0.4, 6);
  const conduitMat = new THREE.MeshStandardMaterial({
    color: 0x222222,
    metalness: 0.8,
    roughness: 0.3,
  });

  for (const rack of racks) {
    const { x, z } = rack.position;
    // Housing mounted right under the ceiling slab
    const housing = new THREE.Mesh(housingGeo, beamMat);
    housing.position.set(x, CEIL_Y - 0.1, z);
    scene.add(housing);

    // Glowing fixture face pointing down
    const face = new THREE.Mesh(faceGeo, faceMat);
    face.rotation.x = Math.PI / 2;
    face.position.set(x, CEIL_Y - 0.201, z);
    scene.add(face);

    // Small mounting rod to ceiling slab
    const rod1 = new THREE.Mesh(conduitGeo, conduitMat);
    rod1.position.set(x - 0.4, CEIL_Y + 0.1, z);
    scene.add(rod1);
    const rod2 = new THREE.Mesh(conduitGeo, conduitMat);
    rod2.position.set(x + 0.4, CEIL_Y + 0.1, z);
    scene.add(rod2);
  }
}

function makeBayLeds(scene, racks) {
  const slots = [];
  for (const rack of racks) {
    const w = rack.userData.w;
    const h = rack.userData.h;
    for (let i = 0; i < 8; i++) {
      const u = 182 / 256;
      const v = 1 - (44 + i * 52 + 33) / 512;
      slots.push({
        rx: rack.position.x,
        ry: rack.position.y,
        rz: rack.position.z,
        x: (u - 0.5) * w * 0.9,
        y: h * 0.47 + (v - 0.5) * h * 0.86,
        z: 0.47,
        color: i === 2 ? 0xff5f56 : 0x3ddc84,
        freq: 0.7 + Math.random() * 4.2,
        phase: Math.random() * Math.PI * 2,
        duty: 0.35 + Math.random() * 0.3,
      });
    }
  }

  const mesh = new THREE.InstancedMesh(
    new THREE.CircleGeometry(0.02, 10),
    new THREE.MeshBasicMaterial({
      toneMapped: false,
      fog: false,
      transparent: true,
    }),
    slots.length,
  );
  const base = new THREE.Color();
  const dummy = new THREE.Object3D();
  slots.forEach((s, k) => {
    dummy.position.set(s.rx + s.x, s.ry + s.y, s.rz + s.z);
    dummy.updateMatrix();
    mesh.setMatrixAt(k, dummy.matrix);
    base.setHex(s.color);
    mesh.setColorAt(k, base);
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  scene.add(mesh);
  return { mesh, slots };
}

function init() {
  const canvas = document.getElementById("scene-canvas");
  if (!canvas) return;

  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: "high-performance",
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1));

  const scene = new THREE.Scene();
  const isDay = isDayInIsrael();

  // Set up 3D sky dome sphere + background & environment maps
  const { dome: skyDome, tex: skyTex } = createSkyDome(THREE, isDay);
  scene.add(skyDome);
  scene.background = skyTex;
  scene.environment = skyTex;

  // Fog smoothly blends distant structures into the light horizon glow
  const currentFogColor = isDay ? FOG_DAY : BG;
  scene.fog = new THREE.Fog(currentFogColor, 40, 150);

  const camera = new THREE.PerspectiveCamera(46, 1, 0.1, 220);
  // Default spawn: Grand Lobby / Street Level (main page view)
  camera.position.set(...POSES.street.pos);

  // Bloom: tuned threshold and radius so interior tower glows bloom cleanly
  const composer = new EffectComposer(renderer);
  const renderPass = new RenderPass(scene, camera);
  const bloom = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth / 3, window.innerHeight / 3),
    isDay ? 0.28 : 0.42,
    isDay ? 0.42 : 0.48,
    isDay ? 0.82 : 0.72,
  );
  const outputPass = new OutputPass();
  composer.addPass(renderPass);
  composer.addPass(bloom);
  composer.addPass(outputPass);

  // Global Lighting & Tower Illumination (Day vs Night balance)
  scene.add(
    new THREE.AmbientLight(isDay ? 0xcbe2f8 : 0x94a3b8, isDay ? 2.6 : 2.4),
  );
  scene.add(
    new THREE.HemisphereLight(
      isDay ? 0xf0f7ff : 0xbae6fd,
      isDay ? 0x475569 : 0x1e293b,
      isDay ? 1.5 : 1.4,
    ),
  );
  const keyLight = new THREE.DirectionalLight(
    isDay ? 0xfef9c3 : 0xf1f5f9,
    isDay ? 2.4 : 2.2,
  );
  keyLight.position.set(16, 50, 24);
  scene.add(keyLight);

  const fillLight = new THREE.DirectionalLight(
    isDay ? 0x93c5fd : 0x38bdf8,
    isDay ? 1.6 : 1.2,
  );
  fillLight.position.set(-18, 30, -20);
  scene.add(fillLight);

  // --- 1. Architectural Tower Model ---
  const building = buildBuilding(scene);

  // --- 2. Floor 1: Server Farm Hall (Base Y: 10.0) ---
  const SERVER_BASE_Y = 10.0;
  const hallFills = [];
  for (const [fx, fz, fi] of [
    [-6, -4, 12],
    [6, -4, 12],
    [0, -11, 14],
    [-5, -15, 10],
    [5, -15, 10],
  ]) {
    const fill = new THREE.PointLight(0x66ccf0, fi, 18, 1.8);
    fill.position.set(fx, SERVER_BASE_Y + 1.4, fz);
    scene.add(fill);
    hallFills.push(fill);
  }

  // Server Floor Grid inside F1
  const grid = new THREE.GridHelper(30, 30, 0x2b3b4e, 0x1c2735);
  grid.position.set(0, SERVER_BASE_Y + 0.001, -6);
  scene.add(grid);

  const hero = makeRack({ hero: true });
  hero.position.set(0, SERVER_BASE_Y, 0);
  hero.userData.section = "about";
  scene.add(hero);

  const clickTargets = [hero];
  const racksBySection = { about: hero };
  for (const [section, at] of Object.entries(NAV_RACKS)) {
    const rack = makeRack();
    rack.position.set(at[0], at[1], at[2]);
    rack.userData.section = section;
    scene.add(rack);
    clickTargets.push(rack);
    racksBySection[section] = rack;
  }

  // Focus spotlight for Server Hall
  const focusLight = new THREE.SpotLight(ACCENT, 45, 14, 0.62, 0.5, 1.8);
  focusLight.position.set(0, SERVER_BASE_Y + 3.6, 8.6);
  scene.add(focusLight);
  scene.add(focusLight.target);
  const focusPos = new THREE.Vector3();
  let focusRack = hero;

  const heroLight = new THREE.PointLight(ACCENT, 0, 6, 2);
  heroLight.position.set(0, SERVER_BASE_Y + 2.5, 1.0);
  scene.add(heroLight);

  const bgRacks = [];
  for (let row = 0; row < 4; row++) {
    for (let col = -5; col <= 5; col++) {
      const x = col * 2.6 + (row % 2 ? 1.3 : 0);
      if (row === 0 && Math.abs(col) < 2) continue;
      if (row >= 2 && Math.abs(x) < 4.0) continue;
      const rack = makeRack();
      rack.position.set(x, SERVER_BASE_Y, -9 - row * 4.5);
      scene.add(rack);
      bgRacks.push(rack);
    }
  }

  const orphan = makeRack();
  orphan.position.set(0, SERVER_BASE_Y, -5);
  scene.add(orphan);
  bgRacks.push(orphan);

  const blinkers = [...clickTargets, ...bgRacks];
  const bayLeds = makeBayLeds(scene, blinkers);
  const tmpCol = new THREE.Color();

  const allRacks = [...clickTargets, ...bgRacks];
  buildCeiling(scene, allRacks, SERVER_BASE_Y);

  const labelTexCache = {};
  if (window.SFS_LOGO_URI) {
    new THREE.TextureLoader().load(window.SFS_LOGO_URI, (tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        color: 0x6f6f6f,
        toneMapped: false,
      });
      for (const rack of allRacks) {
        const w = rack.userData.w;
        const h = rack.userData.h;
        const s = w * 0.21 * 0.6;
        const badge = new THREE.Mesh(new THREE.PlaneGeometry(s, s), mat);
        badge.position.set(w / 2 - w * 0.09 - s / 2, h * 0.955, 0.47);
        rack.add(badge);
        const section = rack.userData.section;
        if (section) {
          if (!labelTexCache[section])
            labelTexCache[section] = makeLabelTexture(section);
          const lw = w * 0.52,
            lh = lw * (128 / 512);
          const label = new THREE.Mesh(
            new THREE.PlaneGeometry(lw, lh),
            new THREE.MeshBasicMaterial({
              map: labelTexCache[section],
              transparent: true,
              color: 0x6f6f6f,
              toneMapped: false,
            }),
          );
          label.position.set(
            w / 2 - w * 0.09 - s / 2 - lw / 2 - 0.02,
            h * 0.955,
            0.47,
          );
          rack.add(label);
        }
      }
    });
  }

  // Cable Trays & live data pulses in F1
  const housingMat = new THREE.MeshStandardMaterial({
    color: 0x0a0e14,
    roughness: 0.6,
    metalness: 0.6,
  });
  const tray = new THREE.Mesh(new THREE.BoxGeometry(32, 0.08, 0.4), housingMat);
  tray.position.set(0, SERVER_BASE_Y + 0.04, -8.5);
  scene.add(tray);

  const cableMat = new THREE.MeshStandardMaterial({
    color: 0x05070a,
    roughness: 0.9,
  });
  const cableCurves = [];
  for (const rack of clickTargets) {
    const p = rack.position;
    const zBack = p.z - 0.45;
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(p.x, SERVER_BASE_Y + 0.1, zBack),
      new THREE.Vector3(p.x * 1.02, SERVER_BASE_Y + 0.05, zBack - 1.2),
      new THREE.Vector3(p.x * 0.8, SERVER_BASE_Y + 0.05, (zBack - 8.5) / 2),
      new THREE.Vector3(p.x * 0.5, SERVER_BASE_Y + 0.05, -8.5),
    ]);
    scene.add(
      new THREE.Mesh(new THREE.TubeGeometry(curve, 24, 0.03, 6), cableMat),
    );
    cableCurves.push(curve);
  }

  const pulseGeo = new THREE.SphereGeometry(0.04, 8, 8);
  const pulseMat = new THREE.MeshBasicMaterial({
    color: ACCENT,
    toneMapped: false,
    fog: false,
  });
  const pulses = [];
  for (const curve of cableCurves) {
    for (let k = 0; k < 3; k++) {
      const mesh = new THREE.Mesh(pulseGeo, pulseMat);
      scene.add(mesh);
      pulses.push({
        mesh,
        curve,
        offset: k / 3,
        speed: 0.12 + Math.random() * 0.08,
        dir: pulses.length % 2 === 0 ? 1 : -1,
      });
    }
  }

  // --- 3. Floor 2: Boardroom & Projects Suite (Base Y: 22.0) ---
  const BOARD_BASE_Y = 22.0;
  const boardroom = buildBoardroom(THREE, ACCENT);
  boardroom.group.position.set(0, BOARD_BASE_Y, 42);
  scene.add(boardroom.group);
  const boardAnchor = boardroom.anchor;
  const boardLight = boardroom.ceilLight;
  const boardFills = boardroom.panelLights;
  const boardScreen = boardroom.screen;
  const presenterUpdate = boardroom.presenterUpdate;
  const audienceUpdate = boardroom.audienceUpdate;
  const propsUpdate = boardroom.propsUpdate;

  const PROJECT_LOGOS = {
    antebi: { mark: "AC", name: "Antebi Ceramics", color: "#d97757" },
    automation: { mark: "AI", name: "AI Automation", color: "#66ccf0" },
    site: { mark: "SF", name: "Smart Flow", color: "#66ccf0" },
    tutoring: { mark: "TA", name: "Tutor AI", color: "#e8b44d" },
  };
  const defaultScreenTex = boardScreen.material.map;
  const logoTexCache = {};
  function screenLogoTex(id) {
    const def = PROJECT_LOGOS[id];
    if (!def) return null;
    if (!logoTexCache[id])
      logoTexCache[id] = makeProjectLogoTexture(THREE, def);
    return logoTexCache[id];
  }

  // Client logo + recommendation cards on the board (current UI lang).
  // CLIENT_REVIEWS maps a client id -> logo mark/color + which i18n review
  // (reviews.cN.*) belongs to them. `stars` is the rating they left.
  // Only real, verifiable clients belong here. The two placeholder entries
  // ("smb" / "ecom") were removed: they were invented testimonials, which is
  // both a trust problem and a legal exposure under Israeli consumer law.
  const CLIENT_REVIEWS = {
    antebi: { mark: "AC", color: "#d97757", stars: 5, review: "c1" },
  };
  const clientReviewTexCache = {};
  let boardMode = "default"; // default | logo | review
  let currentReviewId = null;
  function clientReviewTex(id) {
    const def = CLIENT_REVIEWS[id];
    if (!def) return null;
    const lang = document.documentElement.lang || "en";
    const cacheKey = lang + ":" + id;
    if (!clientReviewTexCache[cacheKey]) {
      const t = (k) =>
        window.SFi18n && typeof window.SFi18n.t === "function"
          ? window.SFi18n.t(k)
          : k;
      clientReviewTexCache[cacheKey] = makeClientReviewTexture(THREE, {
        mark: def.mark,
        color: def.color,
        stars: def.stars,
        name: t("reviews." + def.review + ".author"),
        role: t("reviews." + def.review + ".role"),
        quote: t("reviews." + def.review + ".quote"),
      });
    }
    return clientReviewTexCache[cacheKey];
  }
  // Re-render the client review on the board if the UI language flips.
  document.addEventListener("sfs:langchange", () => {
    Object.keys(clientReviewTexCache).forEach(
      (k) => delete clientReviewTexCache[k],
    );
    if (boardMode === "review" && boardScreen && currentReviewId) {
      const tex = clientReviewTex(currentReviewId);
      if (tex) {
        boardScreen.material.map = tex;
        boardScreen.material.needsUpdate = true;
      }
    }
  });

  // --- 4. Floor 3: Penthouse / Executive Suite (Base Y: 34.0 -> center Y: 40.0) ---
  const penthouse = buildPenthouse(scene);
  penthouse.phGroup.position.set(0, 40.0, 0);

  // --- 5. Street Level Environment & Walkers (Ground Y: 0) ---
  const street = buildStreet(scene);

  // --- 6. Maintenance Bots in Server Hall ---
  const botObstacles = allRacks.map((r) => ({
    x: r.position.x,
    z: r.position.z,
    hx: r.userData.w / 2 + 0.5,
    hz: 0.45 + 0.5,
  }));
  const bots = buildBots(THREE, ACCENT, botObstacles, 3);
  bots.group.position.y = SERVER_BASE_Y;
  scene.add(bots.group);

  // --- 7. Interaction ---
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  let hovered = null;
  function pick(event) {
    const r = canvas.getBoundingClientRect();
    pointer.x = ((event.clientX - r.left) / r.width) * 2 - 1;
    pointer.y = -((event.clientY - r.top) / r.height) * 2 + 1;
    raycaster.setFromCamera(pointer, camera);
    const hits = raycaster.intersectObjects(
      [...clickTargets, street.streetGroup],
      true,
    );
    let g = hits[0]?.object;
    while (g) {
      if (g.userData && (g.userData.section || g.userData.isReception))
        return g;
      g = g.parent;
    }
    return null;
  }

  canvas.addEventListener("pointermove", (e) => {
    hovered = pick(e);
    canvas.style.cursor = hovered ? "pointer" : "default";
  });

  canvas.addEventListener("pointerdown", (e) => {
    const targetHit = pick(e);
    if (!targetHit) return;

    if (targetHit.userData.isReception) {
      if (
        window.SFTowerNav &&
        typeof window.SFTowerNav.openDirectoryModal === "function"
      ) {
        window.SFTowerNav.openDirectoryModal();
      }
      return;
    }

    if (targetHit.userData.section) {
      const sec = targetHit.userData.section;
      if (window.SFNav && typeof window.SFNav.open === "function") {
        window.SFNav.open(sec);
      } else {
        window.SFScene.goTo(sec);
        if (
          window.SFTowerNav &&
          typeof window.SFTowerNav.openPanel === "function"
        ) {
          window.SFTowerNav.openPanel(sec);
        }
      }
    }
  });

  // --- 7. Smooth Vertical Camera Tweening State ---
  // Default floor on load / refresh: Grand Lobby & Street Level
  const target = {
    pos: new THREE.Vector3(...POSES.street.pos),
    look: new THREE.Vector3(...POSES.street.look),
  };
  const look = new THREE.Vector3(...POSES.street.look);
  let activeFloor = "street";
  let activeView = "exterior";

  function applyPose(poseName, instant) {
    const pose = POSES[poseName] || POSES.street;
    target.pos.set(...pose.pos);
    target.look.set(...pose.look);
    if (instant) {
      camera.position.copy(target.pos);
      look.copy(target.look);
      camera.lookAt(look);
    }
  }

  function focusFor(section) {
    if (section === "hero") return hero;
    if (section === "work" || section === "clients") return boardAnchor;
    return racksBySection[section] || hero;
  }

  // --- Global API for Tower Navigation & Legacy 2D Sections ---
  window.SFScene = {
    goTo(section) {
      activeView = "interior";
      activeFloor = "server";
      focusRack = focusFor(section);
      applyPose(section, false);
    },
    getRackScreenPos(section, anchor) {
      const obj =
        section === "work" || section === "clients"
          ? boardAnchor
          : racksBySection[section] || hero;
      const w = obj.userData.w || 1.2;
      const h = obj.userData.h || 2.4;
      const A = {
        tl: [-w * 0.34, h * 0.86, 0.46],
        tc: [0, h * 0.86, 0.46],
        tr: [w * 0.34, h * 0.86, 0.46],
        ml: [-w * 0.34, h * 0.5, 0.46],
        mc: [0, h * 0.5, 0.46],
        mr: [w * 0.34, h * 0.5, 0.46],
        bl: [-w * 0.34, h * 0.16, 0.46],
        bc: [0, h * 0.16, 0.46],
        br: [w * 0.34, h * 0.16, 0.46],
      }[anchor] || [0, h * 0.5, 0.46];
      const v = new THREE.Vector3(A[0], A[1], A[2]);
      obj.localToWorld(v);
      v.project(camera);
      if (v.z > 1 || v.z < -1) return null;
      const r = canvas.getBoundingClientRect();
      return {
        x: r.left + (v.x * 0.5 + 0.5) * r.width,
        y: r.top + (-v.y * 0.5 + 0.5) * r.height,
      };
    },
    showProjectLogo(id) {
      const tex = screenLogoTex(id);
      if (tex) {
        boardScreen.material.map = tex;
        boardScreen.material.needsUpdate = true;
        boardMode = "logo";
        focusRack = boardAnchor;
        applyPose("board", false);
      }
    },
    /** Show ONE client's logo + their recommendation (with stars) on the
        board - used when clicking a client card in the Clients panel. */
    showClientReview(id) {
      const tex = clientReviewTex(id);
      if (tex) {
        boardScreen.material.map = tex;
        boardScreen.material.needsUpdate = true;
        boardMode = "review";
        currentReviewId = id;
        focusRack = boardAnchor;
        applyPose("board", false);
      }
    },
    clearBoardLogo() {
      boardScreen.material.map = defaultScreenTex;
      boardScreen.material.needsUpdate = true;
      boardMode = "default";
      currentReviewId = null;
    },
    /** Tower vertical floor navigation (Exterior) */
    goToFloor(floorKey) {
      activeFloor = floorKey;
      activeView = "exterior";
      focusRack = null;
      let poseKey = "street";
      if (floorKey === "street") poseKey = "street";
      else if (floorKey === "server") poseKey = "tower_server";
      else if (floorKey === "boardroom") poseKey = "tower_boardroom";
      else if (floorKey === "penthouse") poseKey = "tower_penthouse";
      applyPose(poseKey, false);
    },
    /** Tower interior glide into floor room (Interior) */
    enterFloorInterior(floorKey) {
      activeFloor = floorKey;
      activeView = "interior";
      if (floorKey === "street") {
        focusRack = null;
        applyPose("lobby_interior", false);
      } else if (floorKey === "server") {
        focusRack = hero;
        applyPose("server_interior", false);
      } else if (floorKey === "boardroom") {
        focusRack = boardAnchor;
        applyPose("boardroom_interior", false);
      } else if (floorKey === "penthouse") {
        focusRack = null;
        applyPose("penthouse_interior", false);
      }
    },
  };

  // --- Animation & Render Loop ---
  // NOTE: deliberately NO idle-pause here. The tower is a passive showcase
  // experience - viewers watch it without touching the mouse, so the scene
  // must keep animating (LEDs, bots, street, camera drift) continuously.
  const clock = new THREE.Clock();
  let rafId = 0;

  function frame() {
    const dt = clock.getDelta();
    const t = clock.getElapsedTime();
    const low = REDUCED;

    // Server LEDs blink
    for (const rack of blinkers) {
      const boost = rack === hovered ? 1.6 : 1;
      const focus =
        rack === focusRack &&
        activeView === "interior" &&
        activeFloor === "server"
          ? 1
          : 0;
      rack.userData.ledBright +=
        (focus - rack.userData.ledBright) * (low ? 0.25 : 0.08);
      rack.userData.ledMat.color
        .setHex(ACCENT)
        .multiplyScalar(
          rack.userData.ledBright *
            boost *
            (low
              ? 1
              : 0.55 +
                0.45 *
                  Math.sin(t * rack.userData.blinkFreq + rack.userData.phase)),
        );
    }

    // Bay status LEDs
    for (let k = 0; k < bayLeds.slots.length; k++) {
      const led = bayLeds.slots[k];
      const s = Math.sin(t * led.freq + led.phase);
      const on = s > 1 - led.duty * 2;
      bayLeds.mesh.setColorAt(
        k,
        tmpCol.setHex(led.color).multiplyScalar(low ? 0.9 : on ? 0.95 : 0.05),
      );
    }
    if (bayLeds.mesh.instanceColor)
      bayLeds.mesh.instanceColor.needsUpdate = true;

    // Smooth continuous camera tweening
    const ease = low ? 0.2 : 0.045;
    camera.position.lerp(target.pos, ease);
    look.lerp(target.look, ease);
    camera.lookAt(look);

    // Focus spotlight lerp
    if (focusRack) {
      const fp = focusRack.position;
      const lightEase = low ? 0.25 : 0.08;
      const focusTarget =
        activeView === "interior" && activeFloor === "server" ? 45 : 0;
      focusLight.intensity += (focusTarget - focusLight.intensity) * lightEase;
      focusLight.position.lerp(
        focusPos.set(fp.x, SERVER_BASE_Y + 3.6, fp.z + 1.2),
        lightEase,
      );
      focusLight.target.position.lerp(
        focusPos.set(fp.x, SERVER_BASE_Y + 0.9, fp.z),
        lightEase,
      );
      heroLight.intensity +=
        ((focusRack === hero ? 0 : 26) - heroLight.intensity) * lightEase;
    }

    // Boardroom lighting & actors
    const boardOn =
      activeFloor === "boardroom" && activeView === "interior" ? 1 : 0;
    const lightEase = low ? 0.25 : 0.08;
    boardLight.intensity +=
      ((boardOn ? 320 : 0) - boardLight.intensity) * lightEase;
    const targetBoardFill = boardOn ? 280 : 24;
    for (const f of boardFills)
      f.intensity += (targetBoardFill - f.intensity) * lightEase;

    if (boardLight.visible !== (boardOn === 1)) {
      boardLight.visible = boardOn === 1;
    }
    for (const f of boardFills) {
      if (!f.visible) f.visible = true;
    }

    // Cable pulses
    for (const p of pulses) {
      const u = low ? p.offset : (t * p.speed + p.offset) % 1;
      p.mesh.position.copy(p.curve.getPointAt(p.dir > 0 ? u : 1 - u));
    }

    // Beacon blink on tower crown
    if (building.beacon) {
      building.beacon.material.opacity = (Math.sin(t * 3.5) + 1) * 0.5;
    }

    // Maintenance bots update
    bots.update(t, low);

    // Penthouse Boss & steam update
    if (penthouse && penthouse.update) {
      penthouse.update(t, low);
    }

    // Street environment & pedestrian walk update (frozen when on upper floors)
    if (street && street.update) {
      const isStreetActive = activeFloor === "street";
      street.update(t, dt, isStreetActive);
    }

    // Boardroom presenter, audience, steam
    presenterUpdate(t, low);
    audienceUpdate(t, low);
    propsUpdate(t, low);

    composer.render();
    if (REDUCED) {
      // Reduced motion: draw one still frame and stop scheduling new ones.
      rafId = 0;
      return;
    }
    rafId = requestAnimationFrame(frame);
  }

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = 0;
    } else if (!rafId) {
      clock.start();
      rafId = REDUCED
        ? (frame(performance.now()), 0)
        : requestAnimationFrame(frame);
    }
  });

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, false);
    composer.setSize(w, h);
    bloom.setSize(w / 3, h / 3);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  }
  window.addEventListener("resize", resize);
  resize();
  // A reduced-motion visitor gets one rendered frame, not a running scene.
  if (REDUCED) {
    frame(performance.now());
    rafId = 0;
  } else {
    rafId = requestAnimationFrame(frame);
  }
}

try {
  if (document.body.classList.contains("mobile")) {
    document.body.classList.add("no-webgl");
  } else {
    if (window.requestIdleCallback) {
      requestIdleCallback(() => init(), { timeout: 300 });
    } else {
      setTimeout(init, 0);
    }
  }
} catch (err) {
  console.error("[SmartFlow] 3D init failed:", err);
  document.body.classList.add("no-webgl");
}
